/**
 * Where a claim sits in its life. One linear axis — who is holding it and whether
 * you are blocked are derived from this, not tracked alongside it.
 *
 * `SUBMITTED` is deliberately absent: submitting is the same instant the claim lands
 * in review, and the customer cannot act on the difference. The backend keeps both.
 *
 * DRAFT through FILED are the real `Claim.Status`. The carrier half is proposed, and
 * deliberately only two stages: `LossRunClaim.Status` is OPEN/CLOSED/UNKNOWN, so
 * nothing finer can ever come off a carrier feed.
 */
export enum ClaimStatus {
  DRAFT = 'DRAFT',
  IN_REVIEW = 'IN_REVIEW',
  FILED = 'FILED',
  OPEN_WITH_CARRIER = 'OPEN_WITH_CARRIER',
  CLOSED = 'CLOSED',
}

/** How a claim ended. "Resolved" hid the difference between paid and denied. */
export enum ClaimOutcome {
  PAID = 'PAID',
  DENIED = 'DENIED',
  CLOSED_NO_PAYMENT = 'CLOSED_NO_PAYMENT',
  WITHDRAWN = 'WITHDRAWN',
}

export const STATUS_LABELS: Record<ClaimStatus, string> = {
  [ClaimStatus.DRAFT]: 'Draft',
  [ClaimStatus.IN_REVIEW]: 'In review',
  [ClaimStatus.FILED]: 'Filed to carrier',
  [ClaimStatus.OPEN_WITH_CARRIER]: 'Open with carrier',
  [ClaimStatus.CLOSED]: 'Closed',
}

export const OUTCOME_LABELS: Record<ClaimOutcome, string> = {
  [ClaimOutcome.PAID]: 'Paid',
  [ClaimOutcome.DENIED]: 'Denied',
  [ClaimOutcome.CLOSED_NO_PAYMENT]: 'Closed without payment',
  [ClaimOutcome.WITHDRAWN]: 'Withdrawn',
}

/** Lifecycle order. Shared with the timeline so the list and tracker cannot drift apart. */
export const STATUS_ORDER: ClaimStatus[] = [
  ClaimStatus.DRAFT,
  ClaimStatus.IN_REVIEW,
  ClaimStatus.FILED,
  ClaimStatus.OPEN_WITH_CARRIER,
  ClaimStatus.CLOSED,
]

/** Anything blocked on the customer floats up; otherwise earliest stage first. */
export function sortForList(claims: ClaimListRow[]): ClaimListRow[] {
  return [...claims].sort((a, b) => {
    if (Boolean(a.needsYou) !== Boolean(b.needsYou)) return a.needsYou ? -1 : 1
    return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
  })
}

export interface ClaimListRow {
  ref: string
  description: string
  status: ClaimStatus
  /** A blocker that can fire at almost any stage — not a status of its own. */
  needsYou?: boolean
  outcome?: ClaimOutcome
  stageNote: string
  date: string
}

export const CLAIM_ROWS: ClaimListRow[] = [
  {
    ref: 'ZUS-A9T',
    description: 'Rear-end collision, Grand Island NE',
    status: ClaimStatus.OPEN_WITH_CARRIER,
    stageNote: 'Being worked by Progressive',
    date: 'Aug 29',
  },
  {
    ref: 'ZUS-B2K',
    description: 'Cargo damage, water intrusion',
    status: ClaimStatus.IN_REVIEW,
    stageNote: 'Your broker is checking coverage',
    date: 'Sep 1',
  },
  {
    ref: 'ZUS-C71',
    description: 'Parking lot scrape, rear bumper',
    status: ClaimStatus.OPEN_WITH_CARRIER,
    needsYou: true,
    stageNote: 'Jane asked for two more photos',
    date: 'Aug 20',
  },
  {
    ref: 'ZUS-F30',
    description: 'Jackknife, ice on US-281',
    status: ClaimStatus.OPEN_WITH_CARRIER,
    stageNote: 'Being worked by Canal',
    date: 'Aug 11',
  },
  {
    ref: 'ZUS-G04',
    description: 'Trailer door torn off, low bridge',
    status: ClaimStatus.OPEN_WITH_CARRIER,
    stageNote: 'Being worked by Progressive',
    date: 'Jul 28',
  },
  {
    ref: 'ZUS-D45',
    description: 'Rollover, US-30 near Kearney',
    status: ClaimStatus.CLOSED,
    outcome: ClaimOutcome.PAID,
    stageNote: 'Paid $18,400 · closed Aug 2',
    date: 'Jul 15',
  },
  {
    ref: 'ZUS-E18',
    description: 'Windshield chip, I-80',
    status: ClaimStatus.CLOSED,
    outcome: ClaimOutcome.CLOSED_NO_PAYMENT,
    stageNote: 'Below your deductible',
    date: 'Jun 2',
  },
]

/** No claims yet — the state most accounts sit in, and one the list must still answer for. */
export const NO_CLAIMS: ClaimListRow[] = []

export const INSURED_NAME = 'Elite Commodity Express'

export interface ClaimDocument {
  name: string
  meta: string
}

export const CLAIM_DOCUMENTS: ClaimDocument[] = [
  { name: 'Ryans Accident.pdf', meta: 'Uploaded by Ted Johnson · Aug 29 · 2.1 MB' },
  { name: 'Police report - Grand Island PD.pdf', meta: 'Added by your broker · Aug 30 · 640 KB' },
  { name: 'Truck damage - front bumper.jpg', meta: 'Uploaded by Ted Johnson · Aug 29 · 4.4 MB' },
]

export const ADJUSTER = {
  initials: 'JD',
  name: 'Jane Dawes',
  meta: 'Progressive Commercial · Property Damage',
  phone: '(770) 555-0148',
  email: 'jane.dawes@progressive.com',
  claimNumber: 'CL-119452',
}

export const CARRIER_NAME = 'Progressive Commercial'
